"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListResolvedTargetsResponseSchema = exports.ResolvedDeviceTargetSchema = exports.ListAppsResponseSchema = exports.AppSummarySchema = exports.AppEnvSummarySchema = exports.AppTargetSummarySchema = exports.AppRingStatusSchema = exports.DeviceLedgerEntrySchema = exports.LedgerSourceSchema = exports.LEDGER_SOURCE = exports.LedgerViaSchema = exports.LEDGER_VIA = exports.CheckinResponseSchema = exports.CheckinProfileSchema = exports.CheckinRequestSchema = exports.ResolvedTargetSchema = exports.DeviceReleaseSchema = exports.RingPointerSchema = exports.SetBuildLabelRequestSchema = exports.BuildRecordSchema = exports.ReleaseEnvSchema = exports.RELEASE_ENVS = exports.ReleaseRingSchema = exports.RELEASE_RINGS = void 0;
exports.buildShortId = buildShortId;
const zod_1 = require("zod");
const hardwareCatalog_1 = require("./hardwareCatalog");
/**
 * Release rings, narrowest blast radius first. A device belongs to exactly one.
 * Deliberately separate from device-config's `assignedChannel`, which governs
 * BRANDING — different decisions about the same device, independently settable.
 */
exports.RELEASE_RINGS = ['canary', 'pilot', 'stable'];
exports.ReleaseRingSchema = zod_1.z.enum(exports.RELEASE_RINGS);
exports.RELEASE_ENVS = ['dev', 'prod'];
exports.ReleaseEnvSchema = zod_1.z.enum(exports.RELEASE_ENVS);
/**
 * One artifact CI published. `hash` is REQUIRED and non-empty: the installer and
 * the device both verify it before applying, so a record without one could never
 * be installed and must not be registrable in the first place.
 * `apkUrl` is optional because web and iOS targets have no APK.
 * `bundleUrl` and `apkUrl` are two DIFFERENT artifacts with two DIFFERENT
 * sha256 values — the OTA content bundle and the installable APK are not
 * the same file. `hash` has always verified `apkUrl`. `bundleHash` is a
 * SEPARATE, OPTIONAL hash for `bundleUrl` so a promotion can hand out a
 * bundle URL together with the hash that actually matches it, instead of
 * pairing `bundleUrl` with the APK's hash.
 */
exports.BuildRecordSchema = zod_1.z.object({
    appId: zod_1.z.string().min(1).max(64),
    env: exports.ReleaseEnvSchema,
    target: zod_1.z.string().min(1).max(64),
    version: zod_1.z.string().min(1).max(64),
    apkUrl: zod_1.z.string().url().optional(),
    bundleUrl: zod_1.z.string().url().optional(),
    hash: zod_1.z.string().min(1).max(128),
    bundleHash: zod_1.z.string().min(1).max(128).optional(),
    versionCode: zod_1.z.number().int().nonnegative().optional(),
    builtAt: zod_1.z.number().int().nonnegative(),
    registeredAt: zod_1.z.number().int().nonnegative().optional(),
    /**
     * Operator-set display name, renameable at any time — "Cash discount fix",
     * "Demo build for Foggy Frog". PURELY cosmetic: nothing resolves, pins or
     * installs by label, so a rename can never move a device. Optional and
     * always will be — every build registered before this field existed has
     * none, and CI does not set one.
     */
    label: zod_1.z.string().min(1).max(120).optional(),
    /**
     * PROVENANCE — what CI built, written by CI, never by an operator.
     *
     * `commit` is the full git sha the artifact was built from (bounded at 64 to
     * accept both sha-1 and a future sha-256 object id). `commitMessage` is the
     * commit SUBJECT ONLY — the first line, never the body: this row is read
     * back on every builds listing and a full commit body could push a DynamoDB
     * item toward the 400KB limit for no benefit. 200 chars is deliberately
     * generous against git's own ~50/72 subject convention; CI truncates rather
     * than failing a registration, because a build that published must always be
     * registrable.
     *
     * BOTH ARE OPTIONAL AND ALWAYS WILL BE. Every build registered before these
     * fields existed has neither, and non-CI callers (there are none today, but
     * the schema must not assume that) need not supply them. Absent means "not
     * recorded", never "no commit" — the UI falls back to the operator `label`.
     *
     * Deliberately NOT a build status: see registerBuild.ts — a row only exists
     * when registration SUCCEEDED, so a stored status could only ever say
     * "success" and would be a column that cannot tell you anything.
     */
    commit: zod_1.z.string().min(1).max(64).optional(),
    commitMessage: zod_1.z.string().min(1).max(200).optional(),
});
/** Month names for {@link buildShortId} — fixed, never locale-dependent. */
const SHORT_ID_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
/**
 * A short, human-readable name for a build — `DEV-20SEP-577`.
 *
 * DERIVED, never stored: there is exactly one rule, applied identically by the
 * service and the UI, so the two can never print different ids for the same
 * build. Nothing is keyed by it — it is a label for humans to say out loud,
 * not an identifier anything resolves by.
 *
 * Built from the CalVer stamp CI bakes in (`2026.09.20+577-dirty` → day, month,
 * build number). A version that is NOT CalVer — the SHA-versioned builds that
 * predate that scheme — has no date or build number to show, so it falls back
 * to the first 7 chars of the artifact hash rather than rendering blank. The
 * fallback is visibly different (`DEV-5BAB3EF`), so nobody can mistake a hash
 * slug for a date.
 */
function buildShortId(build) {
    const env = build.env.toUpperCase();
    const calver = /^(\d{4})\.(\d{2})\.(\d{2})\+(\d+)/.exec(build.version || '');
    if (calver) {
        const month = SHORT_ID_MONTHS[Number(calver[2]) - 1];
        // A month outside 1–12 means the stamp is malformed; fall through to the
        // hash slug rather than printing `DEV-20UNDEFINED-577`.
        if (month)
            return `${env}-${calver[3]}${month}-${calver[4]}`;
    }
    const slug = (build.hash || build.version || '').slice(0, 7).toUpperCase();
    return slug ? `${env}-${slug}` : env;
}
/**
 * `PATCH /builds/label` (release-service, super-admin only).
 *
 * `label` is NULLABLE, not optional: `undefined` would be dropped by
 * JSON.stringify and the stale label would survive the merge, so clearing a
 * label has to be an explicit `null`. The handler REMOVEs the attribute on
 * null rather than storing an empty string.
 */
exports.SetBuildLabelRequestSchema = zod_1.z.object({
    appId: zod_1.z.string().min(1).max(64),
    env: exports.ReleaseEnvSchema,
    target: zod_1.z.string().min(1).max(64),
    version: zod_1.z.string().min(1).max(64),
    label: zod_1.z.string().min(1).max(120).nullable(),
});
/** Which version a ring currently points at, and who put it there. */
exports.RingPointerSchema = zod_1.z.object({
    appId: zod_1.z.string().min(1).max(64),
    env: exports.ReleaseEnvSchema,
    target: zod_1.z.string().min(1).max(64),
    ring: exports.ReleaseRingSchema,
    version: zod_1.z.string().min(1).max(64),
    promotedAt: zod_1.z.number().int().nonnegative(),
    promotedBy: zod_1.z.string().min(1).max(256),
});
/** The release-targeting fields carried on a device record. */
exports.DeviceReleaseSchema = zod_1.z.object({
    releaseRing: exports.ReleaseRingSchema.default('stable'),
    pinnedVersion: zod_1.z.string().min(1).max(64).optional(),
    pinnedAt: zod_1.z.number().int().nonnegative().optional(),
    pinnedBy: zod_1.z.string().min(1).max(256).optional(),
});
/** What the device is told to run, and why — 'pin' beats 'ring'. */
exports.ResolvedTargetSchema = zod_1.z.object({
    version: zod_1.z.string().min(1).max(64),
    url: zod_1.z.string().url(),
    hash: zod_1.z.string().min(1).max(128),
    source: zod_1.z.enum(['pin', 'ring']),
});
/**
 * Device check-in. `appVersion` and `appId` are OPTIONAL: terminals already in
 * the field predate them and must keep checking in unchanged.
 */
/**
 * `/devices/status` (the handler that consumes this schema) is intentionally
 * UNAUTHENTICATED — terminals have no super_admin JWT to send. Every
 * caller-writable free-text field therefore needs an upper bound: without
 * one, anyone who can reach the endpoint could push a single row toward
 * DynamoDB's 400KB item limit for free. The bounds below are generous, not
 * tight — these are human-entered register/location names and an error
 * string, and a bound must never reject a legitimate terminal.
 */
exports.CheckinRequestSchema = zod_1.z.object({
    // Bounded to 256 to MATCH DeviceLedgerEntrySchema's own deviceId/deviceType
    // bounds below: this schema is the ledger row's only producer, so an unbounded
    // value here would let the unauthenticated endpoint write a ledger row that
    // its own schema rejects (and an `sk` of the same unbounded length).
    deviceId: zod_1.z.string().min(1).max(256),
    deviceType: zod_1.z.string().min(1).max(256),
    channel: zod_1.z.enum(['canary', 'stable']).optional(),
    registerName: zod_1.z.string().max(256).optional(),
    locationName: zod_1.z.string().max(256).optional(),
    locationId: zod_1.z.string().max(256).optional(),
    manifestVersion: zod_1.z.number().nullable().optional(),
    bootVersion: zod_1.z.number().nullable().optional(),
    loadingVersion: zod_1.z.number().nullable().optional(),
    bootInstalled: zod_1.z.boolean().optional(),
    error: zod_1.z.string().max(2000).nullable().optional(),
    appVersion: zod_1.z.string().min(1).max(64).optional(),
    appId: zod_1.z.string().min(1).max(64).optional(),
    /**
     * WHAT THE HARDWARE CALLS ITSELF — Android `Build.MODEL` / `Build.BRAND`,
     * e.g. `TC-TOUCH-K10` / `TCANG`. The one identifier on this request nobody
     * typed, so the one that can contradict the others.
     *
     * OPTIONAL and always will be: every terminal in the field predates it, and
     * the web POS has no such thing. Absent means "did not say", never "wrong".
     * Bounded like every other field here — this endpoint is unauthenticated.
     */
    hardwareModel: zod_1.z.string().min(1).max(128).optional(),
    hardwareBrand: zod_1.z.string().min(1).max(128).optional(),
    /**
     * FLEET MONITORING (2026-10-06) — what the till is still holding that has
     * not reached the server: sales in the offline outbox, everything else
     * (receipts, time punches) in `other`, and when the oldest was queued.
     * OPTIONAL: absent means "this build does not report it", never "nothing waiting".
     */
    pendingUploads: zod_1.z.object({
        sales: zod_1.z.number().int().min(0).max(1000000),
        other: zod_1.z.number().int().min(0).max(1000000),
        oldestAt: zod_1.z.number().int().positive().optional(),
    }).optional(),
    /** Where the till's own app update stands right now. Absent = not reported. */
    updateState: zod_1.z.enum(['idle', 'checking', 'downloading', 'ready', 'waiting_for_idle', 'installing', 'failed']).optional(),
    /** The version the till is downloading / holding ready, when `updateState` is past `checking`. */
    updateVersion: zod_1.z.string().min(1).max(64).optional(),
    updateError: zod_1.z.string().max(500).optional(),
});
/**
 * The device profile a till resolved to at check-in — its inventory catalog
 * model. Built ONLY from stored rows (register → unit → model), never from the
 * unauthenticated request. Absent = not resolvable; the till falls back to what
 * it can see for itself.
 */
exports.CheckinProfileSchema = zod_1.z.object({
    modelId: zod_1.z.string().min(1),
    modelName: zod_1.z.string().optional(),
    buildTarget: zod_1.z.string().optional(),
    terminalFormFactor: zod_1.z.string().optional(),
    capabilities: hardwareCatalog_1.DeviceCapabilitiesSchema.optional(),
});
/**
 * `target` absent means "nothing resolved" — the device keeps what it has.
 *
 * `autoUpdate` is ALWAYS present, defaulting to `true`. It is the remote
 * off switch (Task 10): stored on the device row (owned by
 * `setDeviceAutoUpdate`, not by the device's own check-in), never delivered
 * as a fleet command, because a command only reaches a till that is already
 * online and the whole point of the switch is to stop a bad build reaching
 * one that is about to come online. Absent, null or undefined on the stored
 * row all mean ON — only an explicit stored `false` turns it off — so a
 * device that has never been told otherwise keeps updating. `.default(true)`
 * (not required-with-no-default) is what keeps this addition passing CI's
 * breaking-change detector.
 */
exports.CheckinResponseSchema = zod_1.z.object({
    assignedChannel: zod_1.z.enum(['canary', 'stable']),
    releaseRing: exports.ReleaseRingSchema.optional(),
    target: exports.ResolvedTargetSchema.optional(),
    autoUpdate: zod_1.z.boolean().default(true),
    profile: exports.CheckinProfileSchema.optional(),
});
/**
 * How the device's software version changed — installation method.
 *
 * - `install` — an operator-driven install (DeviceInstallPanel / install-device.sh).
 * - `ota`     — an observed transition: the device reported a version DIFFERENT
 *               from the one already stored for it.
 * - `unknown` — a FIRST SIGHTING. The device reported a version and there is no
 *               stored predecessor, so how it got there is genuinely not known.
 *               Recording that as `ota` would be a false claim.
 */
exports.LEDGER_VIA = ['install', 'ota', 'unknown'];
exports.LedgerViaSchema = zod_1.z.enum(exports.LEDGER_VIA);
/** Where the version came from — decision source. */
exports.LEDGER_SOURCE = ['pin', 'ring', 'manifest', 'unknown'];
exports.LedgerSourceSchema = zod_1.z.enum(exports.LEDGER_SOURCE);
/**
 * Device software release ledger. One row per version change — installation or OTA.
 * `toVersion` is REQUIRED and non-empty: an entry that does not say what the device
 * ended up on is worthless and must not be recorded. `fromVersion` is optional because
 * the first sighting of a device has no predecessor. Every caller-writable free-text
 * field needs an upper bound to prevent pushing DynamoDB items toward the 400KB limit.
 */
exports.DeviceLedgerEntrySchema = zod_1.z.object({
    deviceId: zod_1.z.string().min(1).max(256),
    deviceType: zod_1.z.string().min(1).max(256),
    at: zod_1.z.number().int().nonnegative(),
    appId: zod_1.z.string().min(1).max(64).optional(),
    target: zod_1.z.string().min(1).max(64).optional(),
    fromVersion: zod_1.z.string().min(1).max(256).optional(),
    toVersion: zod_1.z.string().min(1).max(256),
    via: exports.LedgerViaSchema,
    source: exports.LedgerSourceSchema,
    env: zod_1.z.enum(['dev', 'prod']).optional(),
    by: zod_1.z.string().max(256).optional(),
});
/**
 * `GET /apps` (release-service) — one summarised call for the whole releases
 * hub instead of the browser fanning out N calls per app/env/target.
 *
 * A ring's pointer (`version`) is reported EVEN WHEN the build record it
 * points at no longer has a matching BUILD# row — `resolved: false`, `build`
 * absent. Phase B shipped the defect where a dangling pointer like this was
 * silently dropped; this shape makes "unresolvable" a fact the caller must
 * see; it can never be inferred by an absent array entry.
 */
exports.AppRingStatusSchema = zod_1.z.object({
    ring: exports.ReleaseRingSchema,
    version: zod_1.z.string().min(1).max(64),
    promotedAt: zod_1.z.number().int().nonnegative(),
    promotedBy: zod_1.z.string().min(1).max(256),
    /** false when no BUILD# record matches `version` for this target. */
    resolved: zod_1.z.boolean(),
    /** Present only when resolved is true. */
    build: exports.BuildRecordSchema.optional(),
});
exports.AppTargetSummarySchema = zod_1.z.object({
    target: zod_1.z.string().min(1).max(64),
    /** The newest registered build for this app/env/target, by builtAt. Absent
     *  only when a ring pointer exists for this target but no build was ever
     *  registered for it (registerBuild rejects a build with no artifact, so
     *  this is a data anomaly, not an expected state — surface it, don't hide it). */
    newestBuild: exports.BuildRecordSchema.optional(),
    rings: zod_1.z.array(exports.AppRingStatusSchema),
});
exports.AppEnvSummarySchema = zod_1.z.object({
    env: exports.ReleaseEnvSchema,
    targets: zod_1.z.array(exports.AppTargetSummarySchema),
});
exports.AppSummarySchema = zod_1.z.object({
    appId: zod_1.z.string().min(1).max(64),
    environments: zod_1.z.array(exports.AppEnvSummarySchema),
});
exports.ListAppsResponseSchema = zod_1.z.object({
    apps: zod_1.z.array(exports.AppSummarySchema),
});
/**
 * `GET /devices/{deviceType}/resolved-targets` (device-config-service, super-admin
 * only). One row per device, carrying the SAME resolved target the device's own
 * check-in would receive — computed by the identical resolveTarget() the check-in
 * path calls, never re-derived. `target: null` means resolveTarget found nothing
 * usable (no ring pointer, dangling pin with no fallback data) — the device is
 * running whatever it already has, same as an absent `target` on CheckinResponse.
 */
exports.ResolvedDeviceTargetSchema = zod_1.z.object({
    deviceId: zod_1.z.string().min(1).max(256),
    target: exports.ResolvedTargetSchema.nullable(),
});
exports.ListResolvedTargetsResponseSchema = zod_1.z.object({
    deviceType: zod_1.z.string().min(1).max(256),
    targets: zod_1.z.array(exports.ResolvedDeviceTargetSchema),
    count: zod_1.z.number().int().nonnegative(),
});
//# sourceMappingURL=releases.js.map